<script setup lang="ts">
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access/types/permissions/policy';
import type { BlockRootContext } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/public';
import type { AnyItem, CompositePrimaryKey, Filter, GlobalCollectionId } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import { cloneDeep } from 'lodash-es';
import { computed, ref } from 'vue';
import { useAppConfig } from '#app/config';
import { usePolicyEntitlementTranslations } from '#layers/access/composables/policy/use-policy-entitlement-translations';
import { usePolicyEntitlements } from '#layers/access/composables/policy/use-policy-entitlements';
import { entitlementKeyMatchesAction, toEntitlementResourceId } from '#layers/access/utils/entitlement-codec';
import { SortDirection } from '#layers/base/public';
import { RenderCollectionDisplayTemplate } from '#layers/blocks/public';
import { useItems } from '#layers/collection-query/public';
import { CollectionReads, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';
import { PageShell } from '#layers/ui-workspace/public';
import { ButtonAdd, DEFAULT_LISTING_PAGE_SIZE, DrawerPage, hasPaginationContent, ListCard, PageSlotProvider, TeleportPageActions } from '#layers/ui/public';

interface PolicyCustomSettingsDrawerProps {
	collectionId: GlobalCollectionId;
	title?: string;
	itemId?: CompositePrimaryKey;
	itemEdits?: AnyItem;
	formMode?: 'submit' | 'emit';
	entitlements: Entitlement[];
	resource: MonospacePolicyEntitlementResource;
	action: PolicyEntitlementAction;
}
const props = defineProps<PolicyCustomSettingsDrawerProps>();
const emit = defineEmits<{
	close: [payload?: FormBlockSuccessEventPayload];
}>();

const appConfig = useAppConfig();
const { findCollectionById } = useWorkspaceSchemaStore();

const collection = computed(() => findCollectionById(props.collectionId));
const blockContext = computed<BlockRootContext>(() => ({
	itemId: props.itemId,
	formMode: props.formMode,
	itemEdits: props.itemEdits,
	pageType: 'detail',
	pageIsFullWidth: false,
	pageDisplayMode: 'drawer',
}));

const { entitlementName, entitlementDescription, resourceTitlePlural, resourceTitleSingular } = usePolicyEntitlementTranslations({
	resource: () => props.resource,
	action: () => props.action,
});

const entitlements = ref<Entitlement[]>(cloneDeep(props.entitlements?.filter((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: props.resource.name, action: props.action.name })) ?? []));
const {
	addEntitlementWildcard,
	removeEntitlement,
	hasWildcardEntitlement,
	selectedResourceIdsForEntitlement,
	removeResourceSelection,
	addResourceSelection,
} = usePolicyEntitlements({ entitlements });

const selectedResourceIds = computed(() => selectedResourceIdsForEntitlement(props.resource, props.action));
const selectedResourcesEnabled = computed(() => Boolean(collection.value) && !hasWildcardEntitlement(props.resource, props.action) && selectedResourceIds.value.length > 0);
const selectedResourcesPageSize = ref(DEFAULT_LISTING_PAGE_SIZE);

const {
	items: selectedResources,
	pageNumber: selectedResourcesPageNumber,
	totalItemsCount: selectedResourcesTotal,
	currentItemsCount: selectedResourcesCurrentCount,
	showLoading: selectedResourcesLoading,
} = useItems({
	collection,
	enabled: selectedResourcesEnabled,
	pageSize: selectedResourcesPageSize,
	query: computed(() => ({
		filter: { id: { _in: selectedResourceIds.value } },
		sort: [{ id: { direction: SortDirection.Ascending } }],
	})),
});

const displayedSelectedResources = computed(() => selectedResourcesEnabled.value ? selectedResources.value : []);

const selectedResourcesHeaderCount = computed(() => {
	if (!selectedResourcesEnabled.value) return 0;
	return selectedResourcesTotal.value ?? selectedResourcesCurrentCount.value;
});

const showSelectedResourcesPagination = computed(() => hasPaginationContent({
	page: selectedResourcesPageNumber.value,
	pageSize: selectedResourcesPageSize.value,
	totalItems: selectedResourcesTotal.value,
	currentItems: selectedResourcesCurrentCount.value,
	loading: selectedResourcesLoading.value,
}));

function toggleEntitlement() {
	if (hasWildcardEntitlement(props.resource, props.action)) {
		removeEntitlement(props.resource, props.action);
	}
	else {
		addEntitlementWildcard(props.resource, props.action);
	}
}

function save() {
	emit('close', {
		key: props.itemId!,
		data: {
			entitlements: entitlements.value,
			resource: props.resource,
			action: props.action,
		},
	});
}

async function handleAddResource() {
	const filter: Filter = {
		id: { _nin: selectedResourceIdsForEntitlement(props.resource, props.action) },
	};

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: props.collectionId,
			selectionMode: 'multiple',
			filter,
		},
	});

	const result = await modal.open().result;

	for (const key of result?.selection ?? []) {
		const id = toEntitlementResourceId(key);
		if (id) addResourceSelection(props.resource, props.action, id);
	}
}

function handleRemoveResource(item: AnyItem) {
	const id = toEntitlementResourceId(item);
	if (id) removeResourceSelection(props.resource, props.action, id);
}
</script>

<template>
	<DrawerPage
		:attach-to-route="false"
		:ui="{ container: 'gap-0 2xl:w-200', bodyContent: 'p-0' }"
		:title="entitlementName"
		:description="$t('common.select_thing', { thing: resourceTitleSingular })"
		@close="() => emit('close')"
	>
		<PageSlotProvider>
			<PageShell :context="blockContext">
				<div class="flex flex-col items-stretch gap-6">
					<DNotice
						v-if="entitlementDescription"
						color="neutral"
						:description="entitlementDescription"
					/>
					<DForm
						layout="side-by-side"
						layout-split="1-1"
						:state="{ hasWildcardEntitlement: hasWildcardEntitlement(resource, action) }"
					>
						<DFormField
							:label="$t('common.all_things', { things: resourceTitlePlural })"
							:description="$t('policy.entitlements.enable_all_resources', { resources: resourceTitlePlural })"
						>
							<DSwitch
								:model-value="hasWildcardEntitlement(resource, action)"
								@click="toggleEntitlement"
							/>
						</DFormField>
					</DForm>
					<ListCard v-if="!hasWildcardEntitlement(resource, action)" :items="displayedSelectedResources">
						<template #header>
							<span class="text-xs font-medium text-toned">{{ $t('common.n_items', selectedResourcesHeaderCount) }}</span>
						</template>
						<template #header-actions>
							<div class="-me-2 flex items-center gap-2">
								<ButtonAdd size="sm" :label="$t('common.add_item')" :icon="appConfig.ui.icons.plus" @click="handleAddResource" />
							</div>
						</template>
						<template #item-content="{ item }">
							<div class="flex flex-1 items-center gap-2">
								<RenderCollectionDisplayTemplate
									:key="item.id"
									class="flex-1 text-sm"
									:item
									:collection-id
									:read="CollectionReads.Many"
								/>
								<DButton size="sm" variant="link" color="neutral" icon="lucide:x" @click="handleRemoveResource(item)" />
							</div>
						</template>
						<template v-if="showSelectedResourcesPagination" #footer>
							<DPaginationBar
								v-model:page="selectedResourcesPageNumber"
								v-model:page-size="selectedResourcesPageSize"
								:total-items="selectedResourcesTotal"
								:current-items="selectedResourcesCurrentCount"
								:loading="selectedResourcesLoading"
							/>
						</template>
					</ListCard>
				</div>
			</PageShell>
			<TeleportPageActions>
				<DButton color="neutral" variant="outline" :label="$t('common.cancel')" @click="emit('close')" />
				<DButton :label="$t('common.save')" @click="save" />
			</TeleportPageActions>
		</PageSlotProvider>
	</DrawerPage>
</template>
